// @ts-strict-ignore
import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import type { ChangeEvent, FormEvent, HTMLProps } from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { Skeleton, TextField, type TextFieldProps } from "@aviary";
import { RecentSearches } from "@shared/components/RecentSearches/RecentSearches";
import { Typeahead } from "@shared/components/Typeahead/Typeahead";
import { TypeaheadResultsContainer } from "@shared/components/Typeahead/TypeaheadResultsContainer/TypeaheadResultsContainer";
import type { GroupName } from "@shared/components/Typeahead/shared/TypeaheadGroupName";
import type { RecentSearchesItem } from "@shared/data/queries/RecentSearchesQuery/RecentSearches.query";
import { useRecentSearchesLazyQuery } from "@shared/data/queries/RecentSearchesQuery/RecentSearches.query";
import { useTypeaheadSearchV2LazyQuery } from "@shared/data/queries/TypeaheadSearchQuery/TypeaheadSearchV2.query";
import { e2eAttributes } from "@shared/e2eAttributes";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { useIsTouchscreen } from "@shared/hooks/useIsTouchscreen/useIsTouchscreen";
import { useKeyboardNavigationList } from "@shared/hooks/useKeyboardNavigationList/useKeyboardNavigationList";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import { resetOptimizedMegaMenuDropdown } from "@shared/reactiveVar/useOptimizedMegaMenuDropdown.store";
import type { Typeahead as TypeaheadType } from "@shared/types/graphqlGenerated";

import type { TypeaheadViewAll } from "../../Typeahead/shared/TypeaheadViewAll";

import * as styles from "./SearchInput.styles";

export type TypeaheadResultSelectedV2 = {
  typeaheadSearch: Pick<TypeaheadType, "id" | "name" | "entityType" | "productSlug">;
  searchRank?: number;
  groupSearchRank?: number;
};

const loadingSkeleton = Array.from({ length: 5 }).map((_, i) => (
  <div key={i} css={styles.skeletonLineItem}>
    <Skeleton
      type="typography"
      typographySize="h2"
      css={i === 0 ? styles.skeletonButton : styles.skeletonSubButton}
      margin="topAndBottom"
    />
  </div>
));

interface Props extends HTMLProps<HTMLFormElement> {
  onSearchContentClose: () => void;
  onSearchContentOpen: () => void;
  onSearchValueChange: (value: string) => void;
  onQuerySearch: (recentHistorySearchQuery?: string) => void;
  onClearQuerySearch: () => void;
  onTypeaheadSelection: (query?: string) => void;
  searchValue: string;
  showSearchContent: boolean;
  inputSize?: TextFieldProps["size"];
  inputType?: TextFieldProps["type"];
  placeholderOverride?: string;
  goToProductDetails: (productId: string, productSlug?: string) => void;
  logTypeaheadAnalytics?: (
    entityName: string,
    entityType: string,
    recentSearchRank?: number
  ) => void;
  logSearchApplyEvent: (recentSearchQuery?: string, recentSearchRank?: number) => void;
  isFocused?: boolean;
  showRecentSearches?: boolean;
  catalogType?: "DTP" | "WHOLESALE";
  onOptimizedMegaMenuSearchCallback?: (
    typeahead?: Pick<TypeaheadType, "id" | "name" | "entityType">,
    isQuery?: boolean
  ) => void;
  clearAllPriorSearchParam?: boolean;
}

const SearchInput = ({
  onSearchContentClose,
  onSearchContentOpen,
  onSearchValueChange,
  onQuerySearch,
  onClearQuerySearch,
  onTypeaheadSelection,
  searchValue,
  showSearchContent,
  inputSize = "large",
  inputType = "text",
  placeholderOverride,
  goToProductDetails,
  logTypeaheadAnalytics,
  logSearchApplyEvent,
  isFocused = true,
  showRecentSearches = false,
  catalogType,
  onOptimizedMegaMenuSearchCallback,
  clearAllPriorSearchParam,
  ...rest
}: Props) => {
  const { t } = useTranslation("common");

  const isTouchscreen = useIsTouchscreen();
  const { phoneLarge, widescreen } = useBreakpoints();
  const isMobile = widescreen.lessThan;

  const logAnalyticsEvent = useAnalyticsLogger();

  const containerRef = useRef(null);

  const [execute, { loading, data }] = useTypeaheadSearchV2LazyQuery();

  const [isEurekaRecentSearchesEnabled] = useFlippers("eureka_recent_searches");

  const { isPatient, isClinic } = useSharedGlobalConfig();

  const [getRecentSearchesData, { data: recentSearchesData }] = useRecentSearchesLazyQuery({
    fetchPolicy: "network-only",
    ...(catalogType && { variables: { source: catalogType } }),
    skip: !isEurekaRecentSearchesEnabled || !showRecentSearches,
  });

  const recentSearchesItems: RecentSearchesItem[] =
    recentSearchesData?.currentUser?.recentSearches ?? [];

  const isRecentSearch =
    isEurekaRecentSearchesEnabled && showRecentSearches && searchValue?.length === 0;

  const typeaheadViewAll: TypeaheadViewAll[] = [
    {
      id: "ViewAllProduct",
      groupName: "Product",
      onTrigger: onQuerySearch,
    },
  ];

  const typeaheadResults = data?.viewer?.typeaheadSearchV2 ?? [];
  let keyboardResultSet: Typeahead[] = [
    {
      id: "searchBarId",
      name: "searchBar",
      entityType: "searchBar",
    },
  ];

  const { setActiveSearchType, setTypeaheadAsFilter, setClickedRecentSearchTypeaheadToParam } =
    useCatalogSearch();

  const resultsViewAll: TypeaheadViewAll[] = [];

  typeaheadResults.forEach(group => {
    keyboardResultSet = keyboardResultSet.concat(group?.data);

    const groupViewAll = typeaheadViewAll.find(viewAll => viewAll.groupName === group?.entityType);
    resultsViewAll.push(groupViewAll);

    if (groupViewAll) {
      keyboardResultSet.push({
        id: groupViewAll.id,
        name: groupViewAll.groupName,
        entityType: groupViewAll.groupName,
      });
    }
  });

  const handleEnter = () => {
    if (isRecentSearch) {
      return handleRecentSearchEnter();
    }

    return handleTypeaheadEnter();
  };

  const handleSubmission = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isRecentSearch) {
      return handleRecentSearchEnter();
    }

    return handleTypeaheadEnter();
  };

  const handleTypeaheadEnter = () => {
    if (activeKeyboardIndex === 0) {
      return onQuerySearch();
    }

    const isOnViewAll = typeaheadViewAll.find(viewAll => viewAll.id === activeKeyboardRow?.id);
    if (isOnViewAll) {
      return isOnViewAll.onTrigger();
    }

    if (activeKeyboardRow) {
      const groupings = Object.groupBy(keyboardResultSet, item => item.entityType);
      const groups = Object.fromEntries(
        Object.entries(groupings)
          .filter(([entityType]) => entityType !== "searchBar")
          .map(([entityType, items]) => [
            entityType,
            items.filter(item => item.id !== "ViewAllProduct"),
          ])
      );

      // get the group rank of the selected item
      let groupItemIndex = 0;
      for (const group of Object.values(groups)) {
        groupItemIndex = group.findIndex(item => item.name === activeKeyboardRow.name);
        if (groupItemIndex >= 0) {
          break;
        }
      }

      // get the overall rank across all groups of the selected item.
      const overallIndex = Object.values(groups)
        .flat()
        .findIndex(item => item.name === activeKeyboardRow.name);

      onTypeaheadResultSelectedV2({
        typeaheadSearch: activeKeyboardRow,
        searchRank: overallIndex + 1,
        groupSearchRank: Number(groupItemIndex) + 1,
      });
    }
  };

  const handleRecentSearchEnter = () => {
    const offsetFirstEmptyItem = activeKeyboardIndex - 1;
    if (!showSearchContent || offsetFirstEmptyItem < 0) {
      return null;
    }

    const recentSearchItem: RecentSearchesItem = recentSearchesItems[offsetFirstEmptyItem];

    if (recentSearchItem) {
      onRecentSearchItemClick(recentSearchItem, activeKeyboardIndex);
    }
  };

  const onFocusCallback = () => {
    if (isEurekaRecentSearchesEnabled && showRecentSearches) {
      onSearchContentOpen();
    }

    resetOptimizedMegaMenuDropdown();
  };

  const {
    itemSelectedIndex: activeKeyboardIndex,
    onKeyDown,
    onFocus,
  } = useKeyboardNavigationList(
    isRecentSearch ? recentSearchesItems.length + 1 : keyboardResultSet.length,
    containerRef,
    onSearchContentClose,
    handleEnter,
    onFocusCallback,
    onSearchContentClose
  );

  const activeKeyboardRow = keyboardResultSet[activeKeyboardIndex];

  const typeaheadFilters = [
    "PRODUCTS",
    "INGREDIENTS",
    "BRANDS",
    "TAXONOMY_CONDITIONS",
    "TAXONOMY_SUPPLEMENT_TYPES",
  ];

  const debouncedSearch = debouncedSearchValue => {
    execute({
      variables: {
        query: debouncedSearchValue,
        filters: {
          list: typeaheadFilters,
        },
      },
    });
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    if (value?.length < 256) {
      onSearchValueChange(value);
    }
  };

  const onClick = () => {
    if (!showSearchContent) {
      onSearchContentOpen();
    } else {
      onSearchContentClose();
    }
  };

  const onTypeaheadResultSelectedV2 = (result: TypeaheadResultSelectedV2) => {
    const { typeaheadSearch, searchRank, groupSearchRank } = result;

    if (isPatient || isClinic) {
      const eventName = isPatient
        ? AnalyticsEvent.PATIENT_TYPEAHEAD_SELECT
        : AnalyticsEvent.STORE_TYPEAHEAD_SELECT;
      logAnalyticsEvent({
        eventName,
        payload: {
          entity_type: typeaheadSearch.entityType,
          search_rank: searchRank,
          group_search_rank: groupSearchRank,
          typeahead_query: searchValue,
          typeahead_query_length: searchValue.length,
        },
      });
    }

    logTypeaheadAnalytics?.(typeaheadSearch?.name, typeaheadSearch?.entityType);
    onOptimizedMegaMenuSearchCallback?.(typeaheadSearch);
    if (typeaheadSearch?.entityType === "Product") {
      goToProductDetails(typeaheadSearch?.id, typeaheadSearch?.productSlug);
    } else {
      onTypeaheadSelection();

      setTypeaheadAsFilter(typeaheadSearch, {}, clearAllPriorSearchParam);
      if (!isMobile) {
        setActiveSearchType("filters");
      }
    }
  };

  const onRecentSearchItemClick = (recentSearch: RecentSearchesItem, recentSearchRank: number) => {
    if (recentSearch.filterableType) {
      logTypeaheadAnalytics?.(
        recentSearch.filterableName,
        recentSearch.filterableType,
        recentSearchRank
      );
    } else {
      logSearchApplyEvent(recentSearch?.query, recentSearchRank);
    }

    if (recentSearch.filterableType === "Product") {
      return goToProductDetails(recentSearch.filterableId);
    }

    setClickedRecentSearchTypeaheadToParam(recentSearch);

    const isQueryOnly = !recentSearch.filterableType;
    isQueryOnly ? onTypeaheadSelection(recentSearch?.query) : onTypeaheadSelection();

    if (!isMobile) {
      setActiveSearchType("filters");
    }
  };

  const groupNameTranslation = (name: GroupName, count: number) => {
    switch (name) {
      case "Product":
        return t(l.sharedCatalog.typeahead.groupName.Product, {
          count,
        });
      case "Ingredient":
        return t(l.sharedCatalog.typeahead.groupName.Ingredient, {
          count,
        });
      case "Brand":
        return t(l.sharedCatalog.typeahead.groupName.Brand, {
          count,
        });
      case "SupplementType":
        return t(l.sharedCatalog.typeahead.groupName.SupplementType, {
          count,
        });
      case "Condition":
        return t(l.sharedCatalog.typeahead.groupName.Condition, {
          count,
        });
      case "TaxonomySupplementType":
        return t(l.sharedCatalog.typeahead.groupName.TaxonomySupplementType, {
          count,
        });
      case "TaxonomyCondition":
        return t(l.sharedCatalog.typeahead.groupName.TaxonomyCondition, {
          count,
        });
    }
  };

  const getGroupNames = (): GroupName[] =>
    typeaheadResults.map(group => {
      const name = group?.entityType;
      const count = group?.data?.length;

      return groupNameTranslation(name, count);
    });

  const renderContent = () => {
    if (!showSearchContent) {
      return null;
    }

    if (isRecentSearch) {
      return (
        <RecentSearches
          getRecentSearchesData={getRecentSearchesData}
          onRecentSearchItemClick={onRecentSearchItemClick}
          keyboardSelectedIndex={activeKeyboardIndex}
          recentSearchesItems={recentSearchesItems}
        />
      );
    }

    if (searchValue?.length > 0) {
      if (loading) {
        return <TypeaheadResultsContainer>{loadingSkeleton}</TypeaheadResultsContainer>;
      }

      return (
        <Typeahead
          query={searchValue}
          results={typeaheadResults}
          highlightRowId={activeKeyboardRow?.id}
          onResultClick={onTypeaheadResultSelectedV2}
          viewAllGroups={resultsViewAll}
          groupNames={getGroupNames()}
        />
      );
    }
  };

  const getPlaceholder = () => {
    if (placeholderOverride) {
      return placeholderOverride;
    }

    if (phoneLarge.greaterThan) {
      return t(l.sharedCommon.SearchAll);
    }

    return t(l.sharedCommon.SearchProducts);
  };

  return (
    <form css={styles.catalogSearch} ref={containerRef} onSubmit={handleSubmission} {...rest}>
      <TextField
        size={inputSize}
        isFocused={!isTouchscreen && isFocused}
        placeholder={getPlaceholder()}
        value={searchValue}
        onFocus={onFocus}
        onChange={handleSearchChange}
        isDebounced
        onDebouncedChange={debouncedSearch}
        hasClearButton={searchValue !== ""}
        onClearField={onClearQuerySearch}
        aria-label={getPlaceholder()}
        onKeyDown={onKeyDown}
        onMouseDown={onClick}
        data-e2e={e2eAttributes.searchProducts}
        isFullWidth
        inputIcon={faSearch}
        type={inputType}
        enterKeyHint="search"
      />
      {renderContent()}
    </form>
  );
};

export { SearchInput };
